import React from "react";
import { useHistory } from "react-router-dom";
import { Box, Typography } from "@material-ui/core";
import {
  ActionButton,
  B2BSpinner,
  BackButton,
  LoadingPopup,
  MobileFloatingButton,
  MobilePopoverCard,
} from "halifax";
import {
  CardPaymentSelectors,
  getChildState,
  getParentState,
  ParentState,
  useCheckoutState,
  useCheckoutStateSelector,
} from "@capone/checkout";

import "./styles.scss";
import { useIsTravelApp, isVesperTenant, getConfigTenant } from "@capone/common";
import clsx from "clsx";
import dayjs from "dayjs";
import { Event, TEvent } from "../../state/events";
import { ActivityMachineContext } from "../../state/types";
import { PATH_BOOK_CONFIRMATION } from "../../../../utils/paths";
import { getLoadingMessage } from "./utils";
import {
  PriceBreakdown,
  TreesCard,
  TravelerSelection,
  ContactInfo,
  ActivityRewardsAndPayment,
  MobileTripReview,
  PrimaryTravelerSelection,
  AdditionalDetails,
} from "..";
import { CONFIRM_AND_BOOK_CTA_TEXT } from "../textConstants";
import {
  AVAILABLE,
  CHECKOUT_DEFAULT_COMM_EXPERIMENT,
  getExperimentVariant,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { CONTINUE_TEXT } from "../../../common/textConstants";
import { ADDITIONAL_DETAILS_HEADING } from "./textConstants";

export const MobileActivityBookWorkflow = () => {
  const history = useHistory();
  const [state, send] = useCheckoutState<TEvent, ActivityMachineContext>();

  const parentState = getParentState(state.value) as ParentState;
  const childState = getChildState(state.value);

  const isCostCoveredBySelectedPaymentMethods = useCheckoutStateSelector(
    CardPaymentSelectors.getIsCostCoveredBySelectedPaymentMethods
  );

  const expState = useExperiments();
  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT
  );
  const isTreesExperiment = treesModalExperiment === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isDefaultCommEnabled =
    getExperimentVariant(expState.experiments, CHECKOUT_DEFAULT_COMM_EXPERIMENT) ===
    AVAILABLE;

  const isVesper = isVesperTenant(getConfigTenant());

  const { isTravelApp } = useIsTravelApp();

  const handleBack = () => {
    send(Event.PREVIOUS);
  };

  const handleContinue = () => {
    send({
      type: Event.SET_INITIAL_BOOK_TIME,
      initialTime: dayjs().toISOString(),
    });
    send(Event.NEXT);
  };

  React.useEffect(() => {
    // update path so header becomes visible for confirmation
    if (parentState === ParentState.bookingConfirmation) {
      history.replace(
        isTravelApp
          ? `${PATH_BOOK_CONFIRMATION}?isTravelApp=true`
          : PATH_BOOK_CONFIRMATION
      );
    }
  }, [parentState]);

  React.useEffect(() => {
    send({ type: Event.SET_PLATFORM, platform: "mobile" });
  }, []);

  const loadingMessage = getLoadingMessage(
    parentState,
    childState || undefined
  );

  return (
    <Box
      className={clsx("mobile-activity-book-workflow-root", {
        xmod: isXmodRedesign,
      })}
    >
      {parentState === ParentState.activityTravelerInformation && (
        <TravelerSelection
          isMobile
          showNonModalContent={false}
          isXmodRedesign={isXmodRedesign}
        />
      )}

      {parentState === ParentState.activityPrimaryTraveler && (
        <PrimaryTravelerSelection
          isMobile
          showNonModalContent={false}
          isXmodRedesign={isXmodRedesign}
        />
      )}

      {!isDefaultCommEnabled &&
        parentState === ParentState.contactInformation && (
          <ContactInfo isMobile contactFormOnly isXmodRedesign={isXmodRedesign} />
        )}

      {parentState === ParentState.activityAdditionalDetails && (
        <MobilePopoverCard
          open
          className={clsx("activity-book-mobile-popover", {
            xmod: isXmodRedesign,
            redesigned: isVesper,
          })}
          fullScreen
          onClose={() => {}}
          topLeftButton={
            !isTravelApp ? (
              <BackButton
                className="activity-book-mobile-popover-back-button"
                onClick={handleBack}
              />
            ) : undefined
          }
          headerElement={<PriceBreakdown isMobile dropdown />}
          showTransition={false}
        >
          <Box className="activity-book-mobile-header">
            <Typography className="mobile-header-title" variant="h3">
              {ADDITIONAL_DETAILS_HEADING}
            </Typography>
          </Box>
          <hr className="heading-rule" />

          <AdditionalDetails />
          <MobileFloatingButton
            className="activity-book-mobile-continue-button"
            onClick={handleContinue}
            wrapperClassName="b2b"
          >
            {CONTINUE_TEXT}
          </MobileFloatingButton>
        </MobilePopoverCard>
      )}

      {parentState === ParentState.cardPayment && (
        <ActivityRewardsAndPayment isMobile isXmodRedesign={isXmodRedesign} />
      )}

      {parentState === ParentState.review && (
        <>
          <Box className="activity-book-review-header">
            {!isTravelApp ? (
              <BackButton
                className="activity-book-review-header-back-button"
                onClick={handleBack}
              />
            ) : undefined}
            <PriceBreakdown isMobile dropdown />
          </Box>

          <MobileTripReview />
          <ActionButton
            onClick={handleContinue}
            message={CONFIRM_AND_BOOK_CTA_TEXT}
            className="activity-mobile-book-button"
            disabled={!isCostCoveredBySelectedPaymentMethods}
          />
          {isTreesExperiment && <TreesCard />}
        </>
      )}

      <LoadingPopup
        classes={["mobile-activity-book-loading-popup"]}
        open={!!loadingMessage}
        message={loadingMessage || ""}
        indicator={B2BSpinner}
        indicatorSize="small"
        verticalAlignment="center"
        fullScreen
      />
    </Box>
  );
};
